import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from './Popover';
import { useDashboardFilters } from './useDashboardFilters';
import { useTenants } from './useTenants';

/**
 * TenantChip — Sprint 3.9
 *
 * Multiselect typeahead chip for the V3 dashboard filter bar.
 * Scoped by the current property selection. Selected tenants are stored
 * in canonical filter state as `tenantIds: []` (see filter-key.js).
 *
 * Behaves like PropertyChip:
 *   - empty selection = "all tenants" (downstream pages decide what that means)
 *   - search bar + checkbox rows
 *   - hides when the user has fewer than 2 tenants accessible (heuristic:
 *     unfiltered list returns <= 1)
 *
 * `singleSelect` (single-tenant dashboards, e.g. /v3/tenant): picking a tenant
 * replaces the selection instead of appending, and the popover closes on pick.
 * Any leftover multi-selection from another page is trimmed to the first id.
 */
export default function TenantChip({ singleSelect = false }) {
    const { t } = useTranslation();
    const { filters, setTenantIds } = useDashboardFilters();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const anchorRef = useRef(null);

    const selectedIds = filters.tenantIds || [];
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // In single-select mode, normalize any pre-existing multi-selection
    // (carried over from a multi-tenant page) down to the first tenant.
    useEffect(() => {
        if (singleSelect && selectedIds.length > 1) {
            setTenantIds([selectedIds[0]]);
        }
    }, [singleSelect, selectedIds, setTenantIds]);

    const { tenants, loading, error } = useTenants(filters.propertyIds, search, { limit: 30 });

    // Keep recently selected tenants visible even when they fall out of the
    // current search result set.
    const stickyRef = useRef(new Map());
    useMemo(() => {
        tenants.forEach(t => stickyRef.current.set(t.id, t));
    }, [tenants]);

    const visible = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const t of tenants) {
            if (seen.has(t.id)) continue;
            seen.add(t.id);
            out.push(t);
        }
        for (const id of selectedIds) {
            if (seen.has(id)) continue;
            const hit = stickyRef.current.get(id);
            if (hit) {
                seen.add(id);
                out.push(hit);
            }
        }
        return out;
    }, [tenants, selectedIds]);

    const label = useMemo(() => {
        if (!selectedIds.length) return t('All tenants');
        if (selectedIds.length === 1) {
            const hit = visible.find(x => x.id === selectedIds[0]) || stickyRef.current.get(selectedIds[0]);
            return hit?.name || t('1 tenant');
        }
        return t('{{count}} tenants', { count: selectedIds.length });
    }, [selectedIds, visible, t]);

    const toggleTid = (id) => {
        if (singleSelect) {
            // Replace selection (toggle off if re-picking the same tenant), then close.
            setTenantIds(selectedSet.has(id) ? [] : [id]);
            setOpen(false);
            return;
        }
        if (selectedSet.has(id)) {
            setTenantIds(selectedIds.filter(x => x !== id));
        } else {
            setTenantIds([...selectedIds, id]);
        }
    };

    const clearAll = () => setTenantIds([]);

    return (
        <div className="v3-df-chip-wrap">
            <button
                ref={anchorRef}
                type="button"
                className={`v3-df-chip${open ? ' is-open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className="v3-df-chip-icon" aria-hidden>🏬</span>
                <span className="v3-df-chip-label">{label}</span>
                <span className="v3-df-chip-caret" aria-hidden>▾</span>
            </button>

            <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} align="left">
                <div className="v3-df-popover-title">{t('Tenants')}</div>

                <input
                    type="text"
                    className="v3-df-search"
                    placeholder={t('Search by name or code…')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                />

                <div className="v3-df-popover-actions">
                    <span style={{ flex: 1, fontSize: 11, color: 'var(--v3-text-muted)' }}>
                        {filters.propertyIds?.length
                            ? (filters.propertyIds.length === 1
                                ? t('Scoped to 1 property')
                                : t('Scoped to {{count}} properties', { count: filters.propertyIds.length }))
                            : t('All properties')}
                    </span>
                    <button
                        type="button"
                        className="v3-df-link"
                        onClick={clearAll}
                        disabled={!selectedIds.length}
                    >
                        {t('Clear')}
                    </button>
                </div>

                <div className="v3-df-list">
                    {loading && <div className="v3-df-empty">{t('Loading…')}</div>}
                    {error && <div className="v3-df-empty v3-df-error">{error}</div>}
                    {!loading && !error && visible.length === 0 && (
                        <div className="v3-df-empty">{t('No tenants match.')}</div>
                    )}
                    {!loading && !error && visible.map(t => {
                        const checked = selectedSet.has(t.id);
                        return (
                            <label key={t.id} className="v3-df-row">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleTid(t.id)}
                                />
                                <span className="v3-df-row-name">{t.name}</span>
                                {t.code && <span className="v3-df-row-code">{t.code}</span>}
                            </label>
                        );
                    })}
                </div>
            </Popover>
        </div>
    );
}
